import { Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { RegionalHubLinks } from "@/components/RegionalHubLinks";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import type { RegionalHub, TradeGuide } from "@/lib/regionalHubs";
import { TRADE_GUIDES } from "@/lib/regionalHubs";

const SITE_URL = "https://coloradowebimpressions.com";

const CONTRACTOR_ANSWERS = [
  { slug: "how-does-google-maps-decide-distance-for-contractors", label: "How does Google Maps decide distance for contractors?" },
  { slug: "hvac-plumbing-google-ads-vs-seo-budget-colorado", label: "HVAC & plumbing: Google Ads vs SEO budget" },
  { slug: "why-roofing-website-not-converting-storm-calls", label: "Why isn't my roofing website getting storm calls?" },
  { slug: "website-features-that-turn-homeowners-into-quote-requests", label: "Website features that turn homeowners into quotes" },
  { slug: "emergency-vs-scheduled-home-service-seo", label: "Emergency vs scheduled work: SEO differences" },
  { slug: "rank-in-multiple-colorado-towns-without-zip-code-pages", label: "How to rank in several Colorado towns" },
];

export function seoHead(path: string, title: string, description: string, type = "website", scripts?: Array<{ type: string; children: string }>) {
  return {
    ...(scripts ? { scripts } : {}),
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: `${SITE_URL}${path}` },
      { property: "og:type", content: type },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${path}` }],
  };
}

export function RegionalHubPage({ hub }: { hub: RegionalHub }) {
  return (
    <ArticleLayout
      eyebrow="Contractor Marketing Hub"
      title={`${hub.name}: websites, SEO & Google Maps for trades`}
      intro={hub.intro}
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Colorado Coverage", to: "/colorado-coverage" }, { label: hub.name }]}
      path={hub.path}
      faqs={[
        { q: `Do you need a separate page for every town around ${hub.anchorCity}?`, a: "No. We build pages only for towns where you want more work and can really serve. The rest are covered in your Google profile and service-area content." },
        { q: "Which trades do you focus on?", a: "Roofing, HVAC, plumbing, and electrical contractors, plus other home service companies." },
      ]}
    >
      <h2>Towns we cover</h2>
      <p>{hub.coverage.join(" · ")}</p>
      <h2>Local city pages</h2>
      <ul>
        {hub.cityPages.map((c) => (
          <li key={c.to}><Link to={c.to}>{c.label}</Link></li>
        ))}
      </ul>
      <h2>Trade marketing in this area</h2>
      <ul>
        {hub.tradePages.map((c) => (
          <li key={c.to}><Link to={c.to}>{c.label}</Link></li>
        ))}
      </ul>
      <h2>Contractor questions, answered</h2>
      <ul>
        {CONTRACTOR_ANSWERS.map((a) => (
          <li key={a.slug}><Link to="/answers/$slug" params={{ slug: a.slug }}>{a.label}</Link></li>
        ))}
      </ul>
      <h2>Guides for getting more calls</h2>
      <ul>
        {TRADE_GUIDES.map((g) => (
          <li key={g.slug}><Link to={g.slug}>{g.title}</Link></li>
        ))}
      </ul>
      <p>
        Want to see what fits your business? <Link to="/pricing">Request a quote</Link> or call (719) 419-3935.
      </p>
      <div className="not-prose mt-10"><PricingTeaserStrip /></div>
      <RegionalHubLinks excludePath={hub.path} />
    </ArticleLayout>
  );
}

export function TradeGuidePage({ guide }: { guide: TradeGuide }) {
  return (
    <ArticleLayout
      eyebrow={`${guide.trade} Marketing`}
      read="6 min read"
      title={guide.title}
      intro={guide.intro}
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Learning Hub", to: "/learn" }, { label: `${guide.trade} leads` }]}
      path={guide.slug}
      faqs={guide.faqs}
    >
      {guide.sections.map((s) => (
        <div key={s.h}>
          <h2>{s.h}</h2>
          {s.p.map((p) => <p key={p}>{p}</p>)}
        </div>
      ))}
      <h2>Where we help {guide.trade.toLowerCase()} companies</h2>
      <ul>
        <li><Link to={guide.industryPage.to}>{guide.industryPage.label}</Link></li>
        <li><Link to={guide.springsPage.to}>{guide.springsPage.label}</Link></li>
        <li><Link to={guide.southMetroPage.to}>{guide.southMetroPage.label}</Link></li>
      </ul>
      <p>
        Ready to talk? <Link to="/pricing">Request a quote</Link> or call (719) 419-3935.
      </p>
      <RegionalHubLinks excludePath={guide.slug} />
    </ArticleLayout>
  );
}

/** Local search JSON-LD for a regional hub: Service (provider = our LocalBusiness) with every covered city, plus breadcrumbs. */
export function hubSchemaScripts(hub: RegionalHub, description: string) {
  const url = `${SITE_URL}${hub.path}`;
  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: hub.name,
    serviceType: "Contractor website design, local SEO and Google Maps marketing",
    description,
    url,
    provider: {
      "@type": "ProfessionalService",
      "@id": `${SITE_URL}/#organization`,
      name: "Colorado Web Impressions",
      url: SITE_URL,
      telephone: "+1-719-419-3935",
    },
    areaServed: hub.coverage.map((name) => ({
      "@type": "City",
      name,
      containedInPlace: { "@type": "State", name: "Colorado" },
    })),
    audience: { "@type": "BusinessAudience", name: "Home service contractors" },
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Colorado Coverage", item: `${SITE_URL}/colorado-coverage` },
      { "@type": "ListItem", position: 3, name: hub.name, item: url },
    ],
  };
  return [
    { type: "application/ld+json", children: JSON.stringify(service) },
    { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
  ];
}
